import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Trash2, KeyRound } from "lucide-react";

const AdminDemo = () => {
    const [codes, setCodes] = useState([]);
    const [label, setLabel] = useState("");
    const [kind, setKind] = useState("brand");
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/demo/codes").then((r) => setCodes(r.data));
    useEffect(() => { load(); }, []);

    const create = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/demo/codes", { label: label.trim(), kind });
            setLabel("");
            toast.success(`${kind === "creator" ? "Creator" : "Brand"} code ${r.data.code} created`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not create code");
        } finally { setBusy(false); }
    };

    const copyLink = (code) => {
        navigator.clipboard.writeText(`${window.location.origin}/demo?code=${code}`);
        toast.success("Demo link copied to clipboard");
    };

    const revoke = async (code) => {
        if (!window.confirm(`Revoke ${code}? The prospect loses access and their sandbox (campaigns, matches) is wiped.`)) return;
        try {
            await api.delete(`/admin/demo/codes/${code}`);
            toast.success(`${code} revoked`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not revoke");
        }
    };

    const isExpired = (c) => new Date(c.expires_at) < new Date();

    return (
        <DashboardShell role="admin" title="Demo access" subtitle="Unique sandbox logins for prospective brands">
            <div className="yc-card p-6">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-yc-blue" />
                    <h3 className="font-heading text-xl font-bold">Generate a demo code</h3>
                </div>
                <p className="text-sm text-yc-ink/70 mt-2">
                    <strong>Brand demos</strong> get a sandbox brand account: fictional creator roster, instant matching and simulated payments. <strong>Creator demos</strong> get an approved creator account with open briefs, an active placement workspace and sample earnings. Codes expire after 14 days. Demo activity never touches your real data or dashboard numbers.
                </p>
                <div className="flex gap-2 mt-5">
                    {[["brand", "Brand demo"], ["creator", "Creator demo"]].map(([k, lbl]) => (
                        <button key={k} data-testid={`demo-kind-${k}`} onClick={() => setKind(k)}
                            className={`yc-badge cursor-pointer transition-colors ${kind === k ? "bg-yc-blue text-white" : "bg-white"}`}>
                            {lbl}
                        </button>
                    ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <input
                        data-testid="demo-label-input"
                        className="yc-input flex-1"
                        placeholder={kind === "creator" ? "Creator name (e.g. Jamie Woods)" : "Prospect name / company (e.g. Acme Drinks)"}
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") create(); }}
                    />
                    <button data-testid="demo-generate-btn" disabled={busy} onClick={create} className="yc-btn-primary disabled:opacity-50">
                        <Plus className="w-4 h-4 mr-2" /> Generate code
                    </button>
                </div>
            </div>

            <div className="yc-card p-6">
                <h3 className="font-heading text-xl font-bold">Active codes ({codes.length})</h3>
                <div className="mt-4 space-y-3">
                    {codes.map((c) => (
                        <div key={c.code} data-testid={`demo-code-row-${c.code}`} className={`border-2 border-yc-ink rounded-2xl p-4 flex flex-wrap items-center gap-3 ${isExpired(c) ? "opacity-50 bg-yc-cream" : "bg-white"}`}>
                            <div className="font-mono font-black tracking-widest text-lg">{c.code}</div>
                            <span className={`yc-badge text-[10px] ${c.kind === "creator" ? "bg-yc-pink text-white" : "bg-yc-blue text-white"}`}>{c.kind === "creator" ? "Creator" : "Brand"}</span>
                            <div className="flex-1 min-w-[140px]">
                                <div className="font-semibold text-sm">{c.label}</div>
                                <div className="text-xs text-yc-ink/60">
                                    {isExpired(c) ? "Expired" : `Expires ${new Date(c.expires_at).toLocaleDateString()}`} · {c.login_count || 0} login{(c.login_count || 0) === 1 ? "" : "s"}
                                </div>
                            </div>
                            <button data-testid={`demo-copy-${c.code}`} onClick={() => copyLink(c.code)} className="yc-btn-secondary text-xs py-2 px-3">
                                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
                            </button>
                            <button data-testid={`demo-revoke-${c.code}`} onClick={() => revoke(c.code)} className="flex items-center gap-1.5 text-xs font-bold text-yc-pink border-2 border-yc-pink rounded-full px-3 py-2 hover:bg-yc-pink hover:text-white transition-colors">
                                <Trash2 className="w-3.5 h-3.5" /> Revoke
                            </button>
                        </div>
                    ))}
                    {codes.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-6">No demo codes yet — generate your first one above.</div>}
                </div>
            </div>
        </DashboardShell>
    );
};

export default AdminDemo;
